import {
  getCachedViewSnapshotForSelections,
  subscribeViewIdOrLayoutTypeForSelections,
} from '@databases/database-state';
import { getAppMode } from '@databases/modes';
import { LegacyReduxSelectionsReader } from '@databases/redux/cellSelections/legacy-selections-reader';
import cellSelectionsSlice from '@databases/redux/cellSelections/slice';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { appStore } from '@databases/redux/store';
import {
  ReduxDatabaseAdapter,
  type PanelStateProvider,
} from '@databases/redux-database-adapter';
import { initializeSelectionsMachine } from '@databases/selections';
import {
  createDatabaseSelectionReducerPolicies,
  LegacyReduxAdapter,
  SelectionsApi,
  SelectionsApiAdapter,
  SelectionsApiReader,
} from '@databases/selections-api';
import { container, getService } from '@databases/service-container';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { createSelectionUiInteractionSink } from '../features/selections/create-selection-ui-interaction-sink';
import { type SelectionFeatureActivationContext } from '../features/types';
import { filterSelectionFeatures } from '../features/utils/filter-selection-features';
import { resolveFeatures } from '../features/utils/resolve-features';

import { getModeConfig } from './get-mode-config';
import { type Recyclable } from './types';

function initializeLegacySelectionsRecyclable(): Recyclable {
  return {
    setup(contentId) {
      container.registerSingleton(
        'SelectionsActions',
        () =>
          new LegacyReduxAdapter({
            contentId,
            dispatch: appStore.dispatch,
            sliceActions: cellSelectionsSlice.actions,
          })
      );

      const getState = appStore.getState;
      const getContentId = () => contentId;
      const getDatabaseSnapshot = () => {
        const database = getDatabaseWithCurrentView(getState(), { contentId });

        return {
          entries: database.entries,
          fields: database.fields,
        };
      };

      container.registerSingleton(
        'SelectionsReader',
        () =>
          new LegacyReduxSelectionsReader({
            getState,
            getContentId,
            getDatabaseSnapshot,
          })
      );
    },
    dispose() {},
  };
}

function initializeSelectionsMachineRecyclable(): Recyclable {
  let destroy: VoidFunction;
  let unsubscribePermissions: VoidFunction;
  let unsubscribeViewIdentity: VoidFunction;
  let removeSelectionUiDocumentListeners: VoidFunction | undefined;

  function initialize(currentContentId: string) {
    const permissionsManager = getService('PermissionsManager');
    const panelManager = getService('PanelManager');
    const panelStateProvider: PanelStateProvider = {
      getPanelEntryId: () => {
        if (panelManager.state.panel !== 'EntryViewPanel') {
          return null;
        }
        return panelManager.getData('EntryViewPanel')?.entryId ?? null;
      },
    };

    const databaseAdapter = new ReduxDatabaseAdapter({
      getContentId: () => currentContentId,
      getDatabaseSnapshot: (id) =>
        selectDatabaseDraftByContentId(appStore.getState(), {
          contentId: id,
          includeDeleted: false,
          viewSettings: getCachedViewSnapshotForSelections().settings,
        }),
      getState: appStore.getState,
      panelStateProvider,
    });

    const { setup, machine } = initializeSelectionsMachine({
      reducerPolicies: createDatabaseSelectionReducerPolicies({
        canEditContent: () => permissionsManager.getPermissions().canEditContent,
        adapter: databaseAdapter,
      }),
    });

    const selectionsApi = new SelectionsApi({ machine, adapter: databaseAdapter });

    // Now that we have concrete instances, register them immediately.
    container.registerSingleton('SelectionsApi', () => selectionsApi);
    container.registerSingleton('SelectionsActions', () => {
      return new SelectionsApiAdapter({ selectionsApi });
    });
    container.registerSingleton('SelectionsReader', () => {
      return new SelectionsApiReader({
        selectionsApi,
        adapter: databaseAdapter,
      });
    });

    removeSelectionUiDocumentListeners?.();

    const selectionUiSink = createSelectionUiInteractionSink({
      contentId: currentContentId,
      databaseAdapter,
      canEditDatabaseContent: () => permissionsManager.getPermissions().canEditContent,
    });

    /**
     * Selection UI stabilized metrics use rAF; when the tab is hidden or the page unloads,
     * cancel only this sink's four in-flight refs **silently** (no Reliability rows) so
     * durations are not inflated across background time (see `createSelectionUiInteractionSink`).
     */
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        selectionUiSink.cancelInFlightMetrics();
      }
    };

    const onPageHide = () => {
      selectionUiSink.cancelInFlightMetrics();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    removeSelectionUiDocumentListeners = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };

    const modeConfig = getModeConfig(getAppMode());

    const stopFeatures = setup(({ inputCapture, sharedLoop }) => {
      /**
       * The mode determines which features are enabled.
       * There are also some items that despite the mode, are always enabled
       * as they are required for syncing changes, etc.
       */
      const { modeFeatures, alwaysOnFeatures } = modeConfig.buildSelectionFeatures({
        inputCapture,
        sharedLoop,
        databaseAdapter,
        contentId: currentContentId,
        onSelectionUiInteraction: (event) => selectionUiSink.emit(event),
      });

      const activationContext: SelectionFeatureActivationContext = {
        permissions: permissionsManager.getPermissions(),
        layoutType: getCachedViewSnapshotForSelections().settings.layout.type,
      };
      const activeFeatures = filterSelectionFeatures(
        [...modeFeatures.inputs, ...modeFeatures.effects, ...alwaysOnFeatures],
        activationContext
      );

      return resolveFeatures(activeFeatures);
    });

    destroy = () => {
      removeSelectionUiDocumentListeners?.();
      removeSelectionUiDocumentListeners = undefined;
      selectionUiSink.dispose();
      stopFeatures();
    };
  }

  return {
    setup(contentId) {
      initialize(contentId);

      // Subscribe to permission and view identity/layout changes — rebuild so
      // every feature re-evaluates its isEnabled gate.
      unsubscribePermissions?.();
      unsubscribeViewIdentity?.();

      const permissionsManager = getService('PermissionsManager');

      unsubscribePermissions = permissionsManager.subscribe(() => {
        destroy?.();
        initialize(contentId);
      });

      unsubscribeViewIdentity = subscribeViewIdOrLayoutTypeForSelections(() => {
        destroy?.();
        initialize(contentId);
      });
    },

    dispose() {
      unsubscribePermissions?.();
      unsubscribeViewIdentity?.();
      destroy?.();
    },
  };
}

/**
 * Initializes the selections machine behind a feature gate as a recyclable
 * so it is destroyed and rebuilt when bridgeDatabaseIdentityChanged fires,
 * leaving no highlights or state across database identity changes.
 */
export function initializeRecyclableSelectionsMachine(): Recyclable {
  const experimentIsEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  return experimentIsEnabled
    ? initializeSelectionsMachineRecyclable()
    : initializeLegacySelectionsRecyclable();
}
